import { CommandProps, Editor, Extension } from '@tiptap/core'

/** `null` renders a plain `<ol>` (1. 2. 3.), `'a'` renders `<ol type="a">` (a. b. c.). */
export type OrderedListMarker = null | 'a'

declare module '@tiptap/core' {
   interface Commands<ReturnType> {
      orderedListType: {
         /** Set the marker type on the closest ordered list ancestor of the selection. */
         setOrderedListType: (type: OrderedListMarker) => ReturnType
      }
   }
}

export const OrderedListType = Extension.create({
   name: 'orderedListType',

   addCommands() {
      return {
         setOrderedListType:
            (type: OrderedListMarker) =>
            ({ tr, dispatch }: CommandProps) => {
               // Resolved from tr, not state, so this works as a follow-up link in a chain:
               // toggleOrderedList() has already wrapped/converted the list and moved positions.
               const { $from } = tr.selection

               // Walk up to the *closest* ordered list only. Using updateAttributes() here would
               // hit every orderedList ancestor and retype the outer list of a nested list too.
               for (let depth = $from.depth; depth > 0; depth--) {
                  const node = $from.node(depth)

                  if (node.type.name !== 'orderedList') {
                     continue
                  }

                  if (dispatch) {
                     tr.setNodeMarkup($from.before(depth), undefined, { ...node.attrs, type })
                  }

                  return true
               }

               return false
            },
      }
   },
})

/**
 * The marker of the ordered list the selection sits in.
 * Normalises the attribute to `null` — it is `undefined` on a freshly created list but `null`
 * after a getHTML() -> setContent() round trip, since parseHTML reads a missing attribute as null.
 */
export const getOrderedListMarker = (editor: Editor): OrderedListMarker =>
   (editor.getAttributes('orderedList').type as OrderedListMarker) ?? null

/**
 * Word-like toggle: picking the marker the list already uses turns the list off,
 * picking a different one converts the list in place.
 */
export const applyOrderedList = (editor: Editor, type: OrderedListMarker): void => {
   if (editor.isActive('orderedList')) {
      if (getOrderedListMarker(editor) === type) {
         editor.chain().focus().toggleOrderedList().run()
      } else {
         editor.chain().focus().setOrderedListType(type).run()
      }
      return
   }

   // toggleList() copies no attributes when converting a bullet list into an ordered list,
   // so the marker has to be applied as a second step.
   editor.chain().focus().toggleOrderedList().setOrderedListType(type).run()
}
